import React from 'react';
import { createRoot } from 'react-dom/client';
import {
  BrowserRouter as Router,
  Route,
  Switch,
} from 'react-router-dom';
import {
  createStore,
  applyMiddleware,
  compose,
} from 'redux';
import { Provider } from 'react-redux';
import thunk from 'redux-thunk';
import { createLogger } from 'redux-logger';
import { IntercomProvider } from 'react-use-intercom';
import { PostHogProvider } from 'posthog-js/react';

import reducer from './reducers';

import App from './index';
import storeProvider from './storeProvider';

import Login from './containers/Login';
import Register from './containers/Register';
import PasswordReset from './containers/PasswordReset';
import Share from './containers/Share';
import Page from './containers/Page';
import UploadPage from './containers/UploadPage';
import ExternalAuth from './containers/ExternalAuth';
import AppContent from './AppContent';
import AppAdminContent from './AppAdminContent';
import IntlWrapper from './IntlWrapper';

import { isShare } from './helpers/App';
import PrivateRoute from './helpers/PrivateRoute';
import getConfirmation from './helpers/Confirmation';

const middleware = [thunk];
if (process.env.NODE_ENV !== 'production') {
  middleware.push(createLogger());
}

/* eslint-disable no-underscore-dangle */
const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
/* eslint-enable */
const configureStore = (preloadedState) => {
  const store = createStore(
    reducer,
    preloadedState,
    composeEnhancers(applyMiddleware(...middleware)),
  );
  return store;
};

storeProvider.init(configureStore);
const store = storeProvider.getStore();

if (document.getElementById('stockpress')) {
  const root = createRoot(document.getElementById('stockpress'));
  root.render(
    (
      <Provider store={store}>
        <IntlWrapper>
          <Router getUserConfirmation={getConfirmation}>
            <IntercomProvider
              appId={window.sp.do_not_track ? null : process.env.MIX_INTERCOM_APP_ID}
              autoBoot
              autoBootProps={{
                actionColor: '#28c29a',
                backgroundColor: '#28c29a',
                hideDefaultLauncher: isShare(),
                initializeDelay: 1000,
              }}
              shouldInitialize={!window.sp.do_not_track && process.env.MIX_INTERCOM_APP_ID}
            >
              <PostHogProvider
                apiKey={window.sp.do_not_track ? null : process.env.MIX_PUBLIC_POSTHOG_KEY}
                options={{
                  api_host: process.env.MIX_PUBLIC_POSTHOG_HOST,
                }}
              >
                <App>
                  <Switch>
                    <Route path="/login" component={Login} />
                    <Route path="/register" component={Register} />
                    <Route path="/password-reset" component={PasswordReset} />
                    <Route path="/shares/:shareUuid/file/:fileId" component={Share} />
                    <Route path="/shares/:shareUuid/collections/:collectionUuid" component={Share} />
                    <Route path="/shares/:shareUuid" component={Share} />
                    <Route exact path="/pages/:pageId/search" component={Page} />
                    <Route path="/pages/:pageId" component={Page} />
                    <Route path="/u/:uploadPageId/files/:uploadUuid" component={UploadPage} />
                    <Route path="/u/:uploadPageId" component={UploadPage} />
                    <Route path="/auth/:serviceId" component={ExternalAuth} />
                    <PrivateRoute component={AppContent} />
                  </Switch>
                </App>
              </PostHogProvider>
            </IntercomProvider>
          </Router>
        </IntlWrapper>
      </Provider>
    ),
  );
}

if (document.getElementById('stockpress-admin')) {
  const root = createRoot(document.getElementById('stockpress-admin'));
  root.render(
    (
      <Provider store={store}>
        <IntlWrapper>
          <Router>
            <App>
              <Switch>
                <Route path="/login" component={Login} />
                <Route path="/password-reset" component={PasswordReset} />
                <PrivateRoute component={AppAdminContent} />
              </Switch>
            </App>
          </Router>
        </IntlWrapper>
      </Provider>
    ),
  );
}
